import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables as minetwinTables } from '@sdk/minetwin';
import type {
  OrientationStatus,
  OrientationVersion,
} from '@/generated/minetwin/types';

/**
 * Orientations visible to the current coordinator — full catalog
 * including Draft and Archived rows.
 *
 * Two consumer shapes:
 * 1. **JOIN surfaces** (LiveSessions, ReviewQueue, TraineePortal,
 *    Leadership) resolve display names for already-assigned
 *    orientations. An in-flight or completed assignment whose
 *    orientation has since been moved to Draft or Archived must still
 *    show its name; they always want the full catalog.
 * 2. **The Orientation Assignment picker** TEMPORARILY uses this hook with a
 *    client-side `status === Active` filter while the dedicated
 *    server-side `assignable_orientations` view (Samuel `ed2d4b7`) is
 *    still pending its destructive republish. After that republish,
 *    the picker should swap to `useAssignableOrientations` and drop
 *    the client-side filter — that's the form the server can't drift
 *    from.
 *
 * Orientations are org-wide content — common across every site. The
 * optional `projectId` argument is retained for legacy callers that
 * want to scope the picker to a single authored-on-site, but the modern
 * surfaces call with no argument so the user sees every orientation
 * they have catalog access to. Site selection lives on the assignment,
 * not on the orientation.
 *
 * Wraps `visible_orientations` (scoped server-side by
 * `catalog_project_ids` — OrgAdmin sees every project's orientations).
 * Rows are returned sorted by display name so consumers don't re-sort
 * each render. `status` and `autoAssign` flow through from Samuel's
 * lifecycle work (commits ed2d4b7 / dfea329) so consumers can surface
 * auto_assign affordances or render a status pill on past assignments.
 */
export type OrientationRow = {
  orientationId: string;
  projectId: string;
  displayName: string;
  /** Mine jobs this orientation targets; empty = universal (no occupation filter). */
  occupationTarget: string[];
  description: string;
  status: OrientationStatus;
  autoAssign: boolean;
  /** Single-length mode: always `Default` on anything authored since the
   *  cutover. Kept on the row (the module still carries the column) but no
   *  longer displayed or chosen anywhere — see `@/lib/orientationVersion`. */
  defaultVersion: OrientationVersion;
};

export function useOrientations(projectId?: string): {
  orientations: OrientationRow[];
  isReady: boolean;
} {
  const [rows, isReady] = useTable(
    'minetwin',
    minetwinTables.visible_orientations,
  );

  const orientations = useMemo<OrientationRow[]>(() => {
    const filtered = projectId
      ? rows.filter((r) => r.projectId === projectId)
      : rows.slice();
    return filtered
      .slice()
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .map((r) => ({
        orientationId: r.orientationId,
        projectId: r.projectId,
        displayName: r.displayName,
        occupationTarget: r.occupationTarget,
        description: r.description,
        status: r.status,
        autoAssign: r.autoAssign,
        defaultVersion: r.defaultVersion,
      }));
  }, [rows, projectId]);

  return { orientations, isReady };
}
